import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, ArrowRight } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({ email: '', password: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(formData.email, formData.password);
      navigate('/');
    } catch (err) {
      setError(err.response?.data?.detail || 'Authentication failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative overflow-hidden">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/20 via-black to-black" />
      <div className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: 'linear-gradient(rgba(255,255,255,.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.1) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }}
      />

      <div className="relative z-10 w-full max-w-sm">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img 
            src="https://customer-assets.emergentagent.com/job_va-loan-savings/artifacts/b3617o7k_image.png" 
            alt="Kinfolk Home Loans" 
            className="h-24 w-auto"
          />
        </div>

        {/* Tagline */}
        <div className="text-center mb-10">
          <div className="h-px w-12 bg-emerald-600 mx-auto mb-4" />
          <p className="text-zinc-500 text-sm tracking-widest uppercase">Broker Suite</p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="flex items-center gap-2.5 p-3.5 bg-red-950/40 border border-red-900/50 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-zinc-400 text-sm">Email address</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@kinfolkhomeloans.com"
              value={formData.email}
              onChange={handleInputChange}
              required
              data-testid="login-email"
              className="h-12 bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/20 rounded-lg"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-zinc-400 text-sm">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="Enter your password"
              value={formData.password}
              onChange={handleInputChange}
              required
              data-testid="login-password"
              className="h-12 bg-zinc-900/80 border-zinc-800 text-white placeholder:text-zinc-600 focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600/20 rounded-lg"
            />
          </div>

          <Button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-lg transition-all mt-2"
            size="lg"
          >
            {loading ? (
              <span className="flex items-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Signing in...
              </span>
            ) : (
              <span className="flex items-center gap-2">
                Continue
                <ArrowRight className="w-4 h-4" />
              </span>
            )}
          </Button>
        </form>

        <p className="mt-8 text-center text-xs text-zinc-700">
          Need an account? Contact your administrator.
        </p>
      </div>
    </div>
  );
};

export default Login;
